import { Fragment, PureComponent } from 'react'
import { Link } from 'react-router-dom'

import MomentUtils from '@date-io/moment'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import BlockIcon from '@material-ui/icons/Block'
import CancelIcon from '@material-ui/icons/Cancel'
import InfoIcon from '@material-ui/icons/Info'
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import classNames from 'classnames'
import moment from 'moment'

import { api } from '~/api'
import { DEFAULT_MAX_PRICE_DIFF_RATIO } from '~/consts'
import { getPath } from '~/utils'
import { roundToDecimalPlaces } from '~/utils/number'
import { createPriceValidator, maxPriceInRange, minPriceRatio } from '~/validators/price'
import { get, post } from '../../tools/request'
import { copyObject, debounce, getTranslation, translationsIncludeTerm } from '../../tools/utils'
import ProductSelector from '../containers/product-selector'
import DiscountTableHead from './discount-table-head'
import DiscountTableToolbar from './discount-table-toolbar'

import 'moment/locale/en-gb'
import 'moment/locale/th'

function getMaxDiscountPercentageValue() {
    return DEFAULT_MAX_PRICE_DIFF_RATIO * 100
}

const styles = (theme) => ({
    section: {
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 20,
        paddingRight: 20,
        marginBottom: 20,
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid ' + theme.palette.grey[200],
        marginLeft: -20,
        marginRight: -20,
        paddingLeft: 20,
        paddingBottom: 10,
    },
    textField: {
        marginRight: 25,
    },
    nameField: {
        width: 250,
    },
    discountFields: {
        display: 'flex',
        alignItems: 'baseline',
        padding: '20px 10px',
    },
    channelDiscountMessage: {
        alignSelf: 'center',
    },
    clearIcon: {
        fontSize: 20,
    },
    table: {
        width: '100%',
        tableLayout: 'fixed',
    },
    tableCell: {
        padding: '5px 12px',
    },
    originalPriceColumn: {
        textAlign: 'center',
    },
    bottomPagination: {
        marginBottom: 50,
    },
    bottomBar: {
        position: 'fixed',
        bottom: 0,
        display: 'flex',
        backgroundColor: 'white',
        marginLeft: -20,
        marginRight: -20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 10,
        paddingBottom: 10,
        borderTop: '1px solid ' + theme.palette.grey[200],
        zIndex: 999,
        justifyContent: 'space-between',
        width: 'calc(100% - 200px)',
    },
    saveActions: {
        display: 'flex',
        alignItems: 'center',
    },
    saveProgress: {
        marginRight: 15,
    },
    saveButton: {
        color: 'white',
    },
    noProductsMessage: {
        textAlign: 'center',
        paddingTop: 75,
        paddingBottom: 75,
        paddingLeft: 24,
    },
    noProductsText: {
        color: theme.palette.grey[400],
    },
    tableAddButton: {
        marginTop: 20,
    },
    truncatedDescription: {
        display: '-webkit-box',
        '-webkit-line-clamp': 3,
        '-webkit-box-orient': 'vertical',
        'word-break': 'none',
        overflow: 'hidden',
        hyphens: 'auto',
    },
    mainContainer: {
        overflow: 'auto',
    },
})

const sortByShop = (channelStockUnitDiscounts) => {
    return channelStockUnitDiscounts.sort((a, b) => {
        return a.shopID > b.shopID ? 1 : b.shopID > a.shopID ? -1 : 0
    })
}

const sortShopsByID = (shops) => {
    return shops.sort((shopA, shopB) => {
        return shopA.id > shopB.id ? 1 : shopB.id > shopA.id ? -1 : 0
    })
}

// There may be multile channel stock unit discounts for one shop, this function take 1 for 1 shop
const filterDuplicate = (stockUnitDiscount) => {
    const existingShopIDs = new Set()
    const newStockUnitDiscount = copyObject(stockUnitDiscount)
    newStockUnitDiscount.channel_stock_unit_discounts =
        newStockUnitDiscount.channel_stock_unit_discounts.filter((csud) => {
            if (existingShopIDs.has(csud.shopID)) {
                return false
            } else {
                existingShopIDs.add(csud.shopID)
                return true
            }
        })
    return newStockUnitDiscount
}

const rowsPerPage = 25

class Discount extends PureComponent {
    constructor(props) {
        super(props)

        const storeID = this.props.match.params.store_id

        this.state = {
            discount: {
                name: '',
                start_date: null,
                end_date: null,
                num_products: '',
                store: storeID ? parseInt(storeID, 10) : null,
                channel: null,
                status: 'upcoming',
                is_active: true,
            },
            errors: {
                name: '',
                start_date: '',
                end_date: '',
                bulkDiscountPercentage: '',
                bulkDiscountAmount: '',
            },
            shops: [],
            bulkDiscountPercentage: 0,
            bulkDiscountAmount: 0,
            productDiscounts: [],
            newDiscountProducts: [],
            selected: [],
            page: 0,
            loading: true,
            newDiscount: props.location.pathname.includes('create'),
            displayProductSelector: false,
            saving: false,
            maxPageFetched: 0,
            count: 0,
            next: '',
            previous: '',
            addedProductIDs: new Set(),
            removedProductIDs: new Set(),
            allProductIDs: new Set(),
            discountPricesData: {},
        }

        this.searchProductsDebounced = debounce(this.searchProducts, 500)
    }

    componentDidMount() {
        if (this.state.newDiscount) {
            this.setState({ loading: false })
        } else {
            this.fetchDiscount()
            this.fetchProductDiscounts()
        }
    }

    getCopyFromDiscountId = () => {
        const location = this.props.location
        const queryParams = new URLSearchParams(location.search)
        return queryParams.get('copy_discount_id')
    }

    fetchDiscount = (callback) => {
        const { discount_id } = this.props.match.params
        const { discountAPIURL } = this.props

        fetch(discountAPIURL.replace('discount_id', discount_id))
            .then((response) => response.json())
            .then(({ product_pks, ...discount }) => {
                this.setState({ discount, allProductIDs: new Set(product_pks) }, callback)
            })
    }

    fetchProductDiscounts = (url, page = 0, searchTerm = null, callback = null) => {
        if (!url) {
            const { discount_id } = this.props.match.params
            const { simplifiedProductDiscountsAPIURL } = this.props
            url = simplifiedProductDiscountsAPIURL.replace('discount_id', discount_id)
        }
        this.setState({ loading: true })
        let { shops } = this.state
        const shopIDs = new Set(shops.map((shop) => shop.id))
        const requestParams = searchTerm ? { search: searchTerm } : null
        get(url, requestParams).then((data) => {
            let productDiscounts = data.results
            productDiscounts.forEach((pd) => {
                pd.stock_unit_discounts.forEach((sud) => {
                    sud.channel_stock_unit_discounts.forEach((csud) => {
                        const shop = csud.channel_stock_unit.channel_product.shop
                        if (!shopIDs.has(shop.id)) {
                            shopIDs.add(shop.id)
                            shops.push(shop)
                        }
                        csud.shopID = shop.id
                        csud.errors = {}
                    })

                    const allPricesSame = sud.channel_stock_unit_discounts.every(
                        (csud, _i, arr) => csud.price === arr[0].price
                    )

                    if (allPricesSame && sud.channel_stock_unit_discounts.length > 0) {
                        const channelStockUnitDiscount = sud.channel_stock_unit_discounts[0]
                        const percentDiscount =
                            1 -
                            channelStockUnitDiscount.price /
                                channelStockUnitDiscount.channel_stock_unit.price
                        sud.percentDiscount = parseInt(Math.round(percentDiscount * 100), 10)
                    } else {
                        sud.percentDiscount = null
                    }
                })
            })

            const csuDiscountPriceMap = new Map()
            productDiscounts.forEach((pd) => {
                pd.stock_unit_discounts.forEach((sud) => {
                    sud.channel_stock_unit_discounts.forEach((csud) => {
                        csuDiscountPriceMap.set(csud.channel_stock_unit.id, csud.price)

                        const shopID = csud.channel_stock_unit.channel_product.shop.id
                        if (!shopIDs.has(shopID)) {
                            shopIDs.add(shopID)
                            shops.push(csud.channel_stock_unit.channel_product.shop)
                        }
                    })
                })
                // add all shops available for this product for editing
                pd.product.channel_products.forEach((cp) => {
                    const shopID = cp.shop.id
                    if (!shopIDs.has(shopID)) {
                        shopIDs.add(shopID)
                        shops.push(cp.shop)
                    }
                })
            })
            shops = sortShopsByID(shops)

            productDiscounts = productDiscounts.map((pd) => {
                const stockUnitDiscounts = this.makePlaceholderStockUnitDiscounts(
                    pd.product.stock_units,
                    shops,
                    pd.product,
                    csuDiscountPriceMap
                )
                return {
                    ...pd,
                    stock_unit_discounts: stockUnitDiscounts,
                }
            })
            if (page > 0) {
                productDiscounts = this.state.productDiscounts.concat(productDiscounts)
            }

            this.setState(
                {
                    productDiscounts,
                    shops,
                    loading: false,
                    page,
                    maxPageFetched: page,
                    count: data.count,
                    next: data.next,
                    previous: data.previous,
                },
                callback
            )
        })
    }

    addPlaceholderStockUnitDiscounts(productDiscounts, shops) {
        productDiscounts.forEach((pd) => {
            const stockUnitIdsInStockUnitDiscounts = pd.stock_unit_discounts.map(
                (sud) => sud.stock_unit.id
            )
            const stockUnitsWithMissingStockUnitDiscounts = pd.product.stock_units.filter(
                (su) => !stockUnitIdsInStockUnitDiscounts.includes(su.id)
            )
            if (stockUnitsWithMissingStockUnitDiscounts.length) {
                const placeholderStockUnitDiscounts = this.makePlaceholderStockUnitDiscounts(
                    stockUnitsWithMissingStockUnitDiscounts,
                    shops,
                    pd.product,
                    null
                )
                const allStockUnitDiscounts = [
                    ...pd.stock_unit_discounts,
                    ...placeholderStockUnitDiscounts,
                ]
                pd.stock_unit_discounts = allStockUnitDiscounts
            }
        })
    }

    handleNameChange = (event) => {
        const discount = copyObject(this.state.discount)
        const name = event.target.value
        discount.name = name

        const errors = copyObject(this.state.errors)

        if (name) {
            errors.name = ''
        } else {
            errors.name = gettext('Cannot be empty')
        }

        this.setState({ discount, errors })
    }

    handleDateTimeChange = (field) => {
        const startDateTooEarlyMessage = 'Start time must be after %(minStartDate)s today'
        const startDateTooEarlyMessageFormat = ngettext(
            startDateTooEarlyMessage,
            startDateTooEarlyMessage,
            1
        )

        return (date) => {
            const errors = copyObject(this.state.errors)

            const startDate = this.state.discount.start_date
            const endDate = this.state.discount.end_date
            const minStartDate = moment().add(2, 'hours')

            const discount = copyObject(this.state.discount)

            if (
                (field == 'start_date' && date.isAfter(endDate)) ||
                (field == 'end_date' && date.isBefore(startDate))
            ) {
                errors.start_date = gettext('Cannot be after end date')
                errors.end_date = gettext('Cannot be before start date')
            } else if (date.isBefore(minStartDate)) {
                errors.start_date = interpolate(
                    startDateTooEarlyMessageFormat,
                    { minStartDate: minStartDate.format('HH:mm') },
                    true
                )
            } else {
                errors.start_date = ''
                errors.end_date = ''
                discount[field] = date.format()
            }

            // automatically set end_date to start date if we're setting start_date and end_date is empty
            if (field == 'start_date' && discount.end_date == null) {
                discount.end_date = discount.start_date
            }

            this.setState({ discount, errors })
        }
    }

    handleChecked = (checked, productDiscount) => {
        const selectedProductIDs = this.state.selected.map((pd) => pd.product.id)
        let newSelected = copyObject(this.state.selected)

        if (checked) {
            if (!selectedProductIDs.includes(productDiscount.product.id)) {
                newSelected.push(productDiscount)
            }
        } else {
            newSelected = newSelected.filter((pd) => pd.product.id != productDiscount.product.id)
        }

        this.setState({ selected: newSelected })
    }

    handleChangePage = (page) => {
        if (page <= this.state.maxPageFetched) {
            this.setState({ page })
        } else {
            this.fetchProductDiscounts(this.state.next, page)
        }
    }

    handleSelectAllClick = (checked) => {
        if (checked) {
            this.setState((_state) => ({ selected: copyObject(this.state.productDiscounts) }))
        } else {
            this.setState({ selected: [] })
        }
    }

    handleAddProducts = () => {
        this.setState({ displayProductSelector: true })
    }

    handleRemoveProducts = () => {
        const { addedProductIDs, removedProductIDs, selected, newDiscountProducts } = this.state
        selected.map((pd) => {
            const productID = pd.product.id
            if (addedProductIDs.has(productID)) {
                addedProductIDs.delete(productID)
            } else {
                removedProductIDs.add(productID)
            }
        })
        this.setState({
            selected: [],
            removedProductIDs,
            addedProductIDs,
            newDiscountProducts: newDiscountProducts.filter((pd) =>
                addedProductIDs.has(pd.product.id)
            ),
        })
    }

    clearDatePicker = (field) => {
        const discount = copyObject(this.state.discount)

        return (e) => {
            e.stopPropagation()
            discount[field] = null
            this.setState({ discount })
        }
    }

    handleSelectedProducts = (products) => {
        const shopIDs = new Set(this.state.shops.map((s) => s.id))
        let shops = copyObject(this.state.shops)
        const { removedProductIDs, addedProductIDs } = this.state

        const newProducts = []
        products.forEach((p) => {
            const productID = p.id
            p.channel_products.forEach((cp) => {
                const shopID = cp.shop.id
                if (!shopIDs.has(shopID)) {
                    shopIDs.add(shopID)
                    shops.push(cp.shop)
                }
            })
            if (removedProductIDs.has(productID)) {
                // this product's removed before and now added back
                removedProductIDs.delete(p.id)
            } else {
                addedProductIDs.add(p.id)
                newProducts.push(p)
            }
        })
        shops = sortShopsByID(shops)

        const addedProductDiscounts = newProducts.map((p) => {
            const stockUnitDiscounts = this.makePlaceholderStockUnitDiscounts(
                p.stock_units,
                shops,
                p,
                null
            )
            const newProductDiscount = {
                product: p,
                stock_unit_discounts: stockUnitDiscounts,
            }
            return newProductDiscount
        })

        let newDiscountProducts = copyObject(this.state.newDiscountProducts)
        newDiscountProducts = newDiscountProducts.concat(addedProductDiscounts)

        this.setState({
            shops,
            displayProductSelector: false,
            addedProductIDs,
            removedProductIDs,
            newDiscountProducts,
        })
    }

    makePlaceholderStockUnitDiscounts(stockUnits, shops, product, priceMap) {
        return stockUnits.map((su) => {
            const channelStockUnitDiscounts = []

            shops.forEach((shop) => {
                const channelProducts = product.channel_products.filter(
                    (cp) => cp.shop.id === shop.id
                )

                if (channelProducts.length > 0) {
                    channelProducts.forEach((channelProduct) => {
                        const channelStockUnit = channelProduct.channel_stock_units.find(
                            (csu) => csu.stock_unit.id === su.id
                        )
                        if (channelStockUnit) {
                            // Need child channel_product on channel_stock_unit
                            // but must delete channel_stock_units on channel_product to avoid circular object
                            const channelProductCopy = copyObject(channelProduct)
                            delete channelProductCopy.channel_stock_units

                            channelStockUnit.channel_product = channelProductCopy
                            channelStockUnit.stock_unit = channelStockUnit.stock_unit.id

                            channelStockUnitDiscounts.push({
                                shopID: shop.id,
                                channel_stock_unit: channelStockUnit,
                                price: priceMap?.get(channelStockUnit.id) || null,
                                errors: {},
                            })
                        } else {
                            // add placeholder
                            channelStockUnitDiscounts.push({
                                shopID: shop.id,
                                channel_stock_unit: null,
                                price: null,
                                errors: {},
                            })
                        }
                    })
                } else {
                    // add placeholder
                    channelStockUnitDiscounts.push({
                        shopID: shop.id,
                        channel_stock_unit: null,
                        price: null,
                        errors: {},
                    })
                }
            })

            sortByShop(channelStockUnitDiscounts)

            return {
                stock_unit: su,
                channel_stock_unit_discounts: channelStockUnitDiscounts,
                percentDiscount: 0,
                errors: {},
            }
        })
    }

    /**
     * @description Update all discount prices by percentage
     */
    handleUpdateAllDiscounts = (value) => {
        if (value < 0 || value > getMaxDiscountPercentageValue()) {
            return
        }

        const { discountPricesData } = this.state
        const productDiscounts = [...this.state.productDiscounts]
        const newDiscountProducts = [...this.state.newDiscountProducts]
        const discountPercent = value / 100

        productDiscounts.concat(newDiscountProducts).forEach((pd) => {
            pd.stock_unit_discounts.forEach((sud) => {
                if (value) {
                    sud.channel_stock_unit_discounts
                        .filter((csud) => Boolean(csud.channel_stock_unit))
                        .forEach((csud) => {
                            let newDiscountPrice = Math.round(
                                csud.channel_stock_unit.price * (1 - discountPercent)
                            )

                            if (newDiscountPrice < 0) {
                                newDiscountPrice = 0
                            }

                            csud.errors = {}
                            csud.price = newDiscountPrice
                            discountPricesData[`${sud.stock_unit.id}_${csud.shopID}`] =
                                newDiscountPrice
                        })
                }

                sud.percentDiscount = parseFloat(value)
            })
        })

        this.setState({
            productDiscounts,
            newDiscountProducts,
            discountPricesData,
            bulkDiscountPercentage: value,
        })
    }

    updateManyDiscountsWithPercent = (productDiscounts, percent, productID, sku) => {
        const updatedDiscountPricesData = {}
        const updatedProductDiscounts = productDiscounts.map((pd) => {
            if (!pd.product.id === productID) {
                return pd
            }

            return {
                ...pd,
                stock_unit_discounts: pd.stock_unit_discounts.map((sud) => {
                    if (sud.stock_unit.sku !== sku) {
                        return sud
                    }

                    const percentDiscount = parseFloat(percent || '0')

                    return {
                        ...sud,
                        percentDiscount,
                        valueDiscount: undefined,
                        channel_stock_unit_discounts: sud.channel_stock_unit_discounts.map(
                            (csud) => {
                                if (!csud.channel_stock_unit) {
                                    return csud
                                }

                                const discountRatio = percentDiscount / 100
                                if (discountRatio <= 0) {
                                    // reset to original price
                                    return {
                                        ...csud,
                                        price: csud.channel_stock_unit.price,
                                        errors: {},
                                    }
                                }

                                const newDiscountPrice = roundToDecimalPlaces(
                                    csud.channel_stock_unit.price * (1 - discountRatio)
                                )

                                const discount = {
                                    ...csud,
                                    price: newDiscountPrice >= 0 ? newDiscountPrice : 0,
                                    errors: {},
                                }

                                const result = this.validatePrice(
                                    newDiscountPrice,
                                    productDiscounts
                                )

                                if (result.success) {
                                    updatedDiscountPricesData[
                                        `${sud.stock_unit.id}_${csud.shopID}`
                                    ] = newDiscountPrice
                                } else {
                                    discount.errors.discountAmount = result.error
                                }

                                return discount
                            }
                        ),
                    }
                }),
            }
        })

        return [updatedProductDiscounts, updatedDiscountPricesData]
    }

    /**
     * @description Get all unique prices from all channel_stock_unit_discounts
     * @param {'original' | 'current'} type - 'original' for original price, 'current' for current input price
     * @returns {number[]}
     */
    getUniquePrices = (type = 'original', productDiscounts, sku = '') => {
        const uniquePrices = productDiscounts.reduce((prices, pd) => {
            pd.stock_unit_discounts.forEach((sud) => {
                if (sku && sud.stock_unit.sku !== sku) {
                    return
                }

                sud.channel_stock_unit_discounts.forEach((csud) => {
                    if (!csud.channel_stock_unit) {
                        return
                    }

                    const price =
                        type === 'original' ? parseFloat(csud.channel_stock_unit.price) : csud.price
                    if (price) {
                        prices.add(price)
                    }
                })
            })

            return prices
        }, new Set())

        return Array.from(uniquePrices)
    }

    /**
     * @description Validate price with all validators
     * @param {number} price
     */
    validatePrice = (price, productDiscounts, sku) => {
        const originalPrices = this.getUniquePrices('original', productDiscounts, sku)
        const validator = createPriceValidator([
            minPriceRatio(originalPrices),
            maxPriceInRange(originalPrices),
        ])

        return validator.validate(price)
    }

    updateDiscountWithPrice = (productDiscounts, newPrice, productID, sku, shopId) => {
        return productDiscounts.map((pd) => {
            if (pd.product.id !== productID) {
                return pd
            }

            return {
                ...pd,
                stock_unit_discounts: pd.stock_unit_discounts.map((sud) => {
                    if (sud.stock_unit.sku !== sku) {
                        return sud
                    }

                    return {
                        ...sud,
                        percentDiscount: undefined,
                        valueDiscount: undefined,
                        channel_stock_unit_discounts: sud.channel_stock_unit_discounts.map(
                            (csud) => {
                                if (csud.shopID !== shopId) {
                                    return csud
                                }

                                const result = this.validatePrice(newPrice, productDiscounts, sku)

                                return {
                                    ...csud,
                                    price: newPrice,
                                    errors: result.success ? {} : { discountAmount: result.error },
                                }
                            }
                        ),
                    }
                }),
            }
        })
    }

    updateManyDiscountsWithPrice = (productDiscounts, newPrice, productID, sku) => {
        const updatedDiscountPricesData = {}
        const updatedProductDiscounts = productDiscounts.map((pd) => {
            if (pd.product.id !== productID) {
                return pd
            }

            return {
                ...pd,
                stock_unit_discounts: pd.stock_unit_discounts.map((sud) => {
                    if (sud.stock_unit.sku !== sku) {
                        return sud
                    }

                    return {
                        ...sud,
                        percentDiscount: undefined,
                        valueDiscount: newPrice,
                        channel_stock_unit_discounts: sud.channel_stock_unit_discounts.map(
                            (csud) => {
                                if (!csud.channel_stock_unit) {
                                    return csud
                                }

                                const discount = {
                                    ...csud,
                                    price: newPrice,
                                    errors: {},
                                }

                                const result = this.validatePrice(newPrice, productDiscounts, sku)
                                if (result.success) {
                                    updatedDiscountPricesData[
                                        `${sud.stock_unit.id}_${csud.shopID}`
                                    ] = newPrice
                                } else {
                                    discount.errors.discountAmount = result.error
                                }

                                return discount
                            }
                        ),
                    }
                }),
            }
        })

        return [updatedProductDiscounts, updatedDiscountPricesData]
    }
    handleBulkDiscountPercentChange = (productID, sku) => {
        return (event) => {
            const value = event.target.valueAsNumber || 0
            if (value < 0 || value > getMaxDiscountPercentageValue()) {
                return
            }

            this.setState((prevState) => {
                const [productDiscounts, updatedDiscountPricesDataExisting] =
                    this.updateManyDiscountsWithPercent(
                        prevState.productDiscounts,
                        value,
                        productID,
                        sku
                    )

                const [newDiscountProducts, updatedDiscountPricesDataNew] =
                    this.updateManyDiscountsWithPercent(
                        prevState.newDiscountProducts,
                        value,
                        productID,
                        sku
                    )

                return {
                    ...prevState,
                    productDiscounts,
                    newDiscountProducts,
                    discountPricesData: {
                        ...prevState.discountPricesData,
                        ...updatedDiscountPricesDataExisting,
                        ...updatedDiscountPricesDataNew,
                    },
                }
            })
        }
    }

    handleBulkDiscountValueChange = (productID, sku) => {
        return (event) => {
            const value = event.target.valueAsNumber || 0
            if (value < 0) {
                return
            }

            this.setState((prevState) => {
                const [productDiscounts, updatedDiscountPricesDataExisting] =
                    this.updateManyDiscountsWithPrice(
                        prevState.productDiscounts,
                        value,
                        productID,
                        sku
                    )

                const [newDiscountProducts, updatedDiscountPricesDataNew] =
                    this.updateManyDiscountsWithPrice(
                        prevState.newDiscountProducts,
                        value,
                        productID,
                        sku
                    )

                return {
                    ...prevState,
                    productDiscounts,
                    newDiscountProducts,
                    discountPricesData: {
                        ...prevState.discountPricesData,
                        ...updatedDiscountPricesDataExisting,
                        ...updatedDiscountPricesDataNew,
                    },
                }
            })
        }
    }

    handleDiscountPriceChange = (product, stockUnit, shopId) => {
        const productId = product.id
        const sku = stockUnit.sku

        return (event) => {
            const value = event.target.valueAsNumber
            if (Number.isNaN(value)) {
                return
            }

            this.setState((prevState) => {
                const productDiscounts = this.updateDiscountWithPrice(
                    prevState.productDiscounts,
                    value,
                    productId,
                    sku,
                    shopId
                )
                const newDiscountProducts = this.updateDiscountWithPrice(
                    prevState.newDiscountProducts,
                    value,
                    productId,
                    sku,
                    shopId
                )

                return {
                    ...prevState,
                    productDiscounts,
                    newDiscountProducts,
                    discountPricesData: {
                        ...prevState.discountPricesData,
                        [`${stockUnit.id}_${shopId}`]: value,
                    },
                }
            })
        }
    }

    isSelected = (productID) => {
        const selectedProductIDs = this.state.selected.map((pd) => pd.product.id)
        return selectedProductIDs.includes(productID)
    }

    searchProducts = (searchTerm) => {
        this.setState({ searchTerm })
        this.fetchProductDiscounts(null, 0, searchTerm)
    }

    searchHandler = (searchTerm) => {
        this.searchProductsDebounced(searchTerm)
    }

    productDiscountHasErrors = () => {
        const discountWithError = this.state.productDiscounts.filter((productDiscount) => {
            const stockUnitDiscountWithErrors = productDiscount.stock_unit_discounts.filter(
                (stockUnitDiscount) => {
                    const csuds = stockUnitDiscount.channel_stock_unit_discounts.filter((csud) => {
                        return csud?.errors && Object.keys(csud.errors).length > 0
                    })
                    return csuds?.length > 0
                }
            )
            return stockUnitDiscountWithErrors?.length > 0
        })

        return discountWithError?.length > 0
    }

    discountComplete = () => {
        const {
            name: name_error,
            start_date: start_date_error,
            end_date: end_date_error,
        } = this.state.errors
        const { name, start_date, end_date } = this.state.discount

        return (
            (!name_error || !start_date_error || !end_date_error) &&
            !!name &&
            !!start_date &&
            !!end_date
        )
    }

    createDiscount = (copyDiscountId) => {
        const { discountCreateAPIURL } = this.props
        const payload = {
            ...this.state.discount,
        }
        if (copyDiscountId) {
            payload.copy_discount_id = copyDiscountId
        }
        return post(discountCreateAPIURL, payload)
    }

    saveProductDiscounts = async () => {
        const { discountPricesData, removedProductIDs } = this.state
        const requestData = {
            channel_stock_unit_discounts: Object.entries(discountPricesData).map(([key, value]) => {
                const subs = key.split('_')
                return {
                    stock_unit_id: Number(subs[0]),
                    shop_id: Number(subs[1]),
                    price: value,
                }
            }),
            deleted_product_ids: Array.from(removedProductIDs),
        }

        return await api.discounts.updateChannelStockUnitDiscount.mutation({
            params: {
                discount_pk: this.state.discount.id,
            },
            body: requestData,
        })
    }

    saveHandler = () => {
        window.analytics.track('Update discount', {
            page: window.location.href,
            feature: 'promotion',
            sub_feature: 'discount',
            trigger: 'button click',
        })

        const copyDiscountId = this.getCopyFromDiscountId()

        this.setState({ saving: true }, () => {
            if (this.state.newDiscount) {
                if (copyDiscountId) {
                    this.createDiscount(copyDiscountId).then((createdDiscount) => {
                        window.location.href = getPath('discount', {
                            discount_id: createdDiscount.id,
                        })
                    })
                } else {
                    this.createDiscount().then((discount) => {
                        this.setState({ discount }, () => {
                            this.saveProductDiscounts().then(() => {
                                window.location.href = getPath('discounts')
                            })
                        })
                    })
                }
            } else {
                this.saveProductDiscounts().then(() => {
                    window.location.href = getPath('discounts')
                })
            }
        })
    }

    getDisplayProductDiscounts = () => {
        const { page, newDiscountProducts, productDiscounts, removedProductIDs } = this.state
        let displayedProductDiscounts = productDiscounts
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .filter((pd) => !removedProductIDs.has(pd.product.id))

        if (page > 0 || newDiscountProducts.length === 0) {
            return displayedProductDiscounts
        }

        // add new product discounts to first page
        const { searchTerm } = this.state
        let filteredNewDiscountProducts = newDiscountProducts
        if (searchTerm) {
            filteredNewDiscountProducts = newDiscountProducts.filter((pd) => {
                if (translationsIncludeTerm(pd.product.translations, searchTerm)) {
                    return true
                }

                return pd.stock_unit_discounts.some((sud) => {
                    if (
                        sud.stock_unit.name &&
                        sud.stock_unit.name.toLowerCase().includes(searchTerm.toLowerCase())
                    ) {
                        return true
                    }

                    if (
                        sud.stock_unit.sku &&
                        sud.stock_unit.sku.toLowerCase().includes(searchTerm.toLowerCase())
                    ) {
                        return true
                    }
                    return false
                })
            })
        }

        return filteredNewDiscountProducts.concat(displayedProductDiscounts)
    }

    getExistedProductIDs = () => {
        const { allProductIDs, addedProductIDs, removedProductIDs } = this.state
        return new Set(
            [...allProductIDs, ...addedProductIDs].filter(
                (productID) => !removedProductIDs.has(productID)
            )
        )
    }

    render() {
        const { classes, language, staticRoot } = this.props
        const { selected, page, count, shops } = this.state

        const storeName = this.state.discount.store
            ? this.props.stores.find((store) => store.id == this.state.discount.store).name
            : ''

        const displayedProductDiscounts = this.getDisplayProductDiscounts()

        moment.locale(language)

        const channelName =
            this.state.discount.channel &&
            this.state.discount.channel.name.charAt(0).toUpperCase() +
                this.state.discount.channel.name.slice(1)
        const channelDiscountMessage = interpolate(
            gettext(
                'This discount was automatically generated from %s. It can be edited, but no further products may be added. Any price changes made will go live immediately.'
            ),
            [channelName]
        )
        const isAddingProductsDisabled = ['WooCommerce', 'Lazada'].includes(
            this.state.discount.channel?.name
        )

        const isCopy = !!this.getCopyFromDiscountId()

        let title
        if (this.state.newDiscount) {
            title = isCopy ? gettext('Copy Discount (%s)') : gettext('New Discount (%s)')
        } else {
            title = gettext('Discount (%s)')
        }
        title = interpolate(title, [storeName])

        return (
            <>
                <Paper className={classes.section}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {title}
                        <Tooltip
                            title={gettext(
                                'Name and dates cannot be changed after discount is saved.'
                            )}
                            placement="right"
                        >
                            <IconButton>
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                    <div className={classes.discountFields}>
                        <TextField
                            className={classNames(classes.nameField, classes.textField)}
                            label={gettext('Name')}
                            autoFocus
                            margin="dense"
                            name="name"
                            type="text"
                            variant="outlined"
                            value={this.state.discount.name}
                            error={Boolean(this.state.errors.name)}
                            helperText={this.state.errors.name}
                            disabled={!this.state.newDiscount}
                            onChange={this.handleNameChange}
                        />
                        {isAddingProductsDisabled ? (
                            <Typography className={classes.channelDiscountMessage} variant="body2">
                                {channelDiscountMessage}
                            </Typography>
                        ) : (
                            <>
                                <MuiPickersUtilsProvider
                                    className={classNames(classes.textField, classes.dateField)}
                                    utils={MomentUtils}
                                    locale={language}
                                    value={this.state.discount.start_date}
                                    moment={moment}
                                >
                                    <DateTimePicker
                                        value={this.state.discount.start_date}
                                        label={gettext('Start date')}
                                        margin="dense"
                                        className={classNames(classes.textField, classes.dateField)}
                                        format="lll"
                                        disabled={!this.state.newDiscount}
                                        error={Boolean(this.state.errors.start_date)}
                                        helperText={this.state.errors.start_date}
                                        disablePast={true}
                                        maxDate={
                                            this.state.discount.end_date
                                                ? this.state.discount.end_date
                                                : moment().add(5, 'y')
                                        }
                                        inputVariant="outlined"
                                        InputProps={{
                                            endAdornment: this.state.newDiscount && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={this.clearDatePicker('start_date')}
                                                    >
                                                        <CancelIcon className={classes.clearIcon} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        onChange={this.handleDateTimeChange('start_date')}
                                    />
                                </MuiPickersUtilsProvider>
                                <MuiPickersUtilsProvider
                                    className={classNames(classes.textField, classes.dateField)}
                                    utils={MomentUtils}
                                    locale={language}
                                    value={this.state.discount.end_date}
                                    moment={moment}
                                >
                                    <DateTimePicker
                                        value={this.state.discount.end_date}
                                        label={gettext('End date')}
                                        margin="dense"
                                        className={classNames(classes.textField, classes.dateField)}
                                        format="lll"
                                        disabled={!this.state.newDiscount}
                                        error={Boolean(this.state.errors.end_date)}
                                        helperText={this.state.errors.end_date}
                                        disablePast={true}
                                        maxDate={moment().add(5, 'y')}
                                        inputVariant="outlined"
                                        InputProps={{
                                            endAdornment: this.state.newDiscount && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={this.clearDatePicker('end_date')}
                                                    >
                                                        <CancelIcon className={classes.clearIcon} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        onChange={this.handleDateTimeChange('end_date')}
                                    />
                                </MuiPickersUtilsProvider>
                            </>
                        )}
                    </div>
                </Paper>
                {(isAddingProductsDisabled || this.discountComplete()) && !isCopy && (
                    <Paper className={classes.mainContainer}>
                        <DiscountTableToolbar
                            rowsPerPage={rowsPerPage}
                            canAddProducts={
                                !this.state.discount.store || !!this.state.discount.channel
                            }
                            numSelected={selected.length}
                            bulkDiscountPercentage={this.state.bulkDiscountPercentage}
                            disabledBulkEdit={Boolean(this.state.discount.channel)}
                            count={count}
                            page={page}
                            loading={this.state.loading}
                            onSearch={this.searchHandler}
                            onChangePage={this.handleChangePage}
                            onRemoveProducts={this.handleRemoveProducts}
                            onAddProducts={this.handleAddProducts}
                            onBulkDiscountPercentChanged={this.handleUpdateAllDiscounts}
                        />
                        <TableContainer>
                            <Table className={classes.table} aria-labelledby="tableTitle">
                                <DiscountTableHead
                                    numSelected={selected.length}
                                    rowCount={this.state.productDiscounts.length}
                                    shops={this.state.shops}
                                    onSelectAllClick={this.handleSelectAllClick}
                                />
                                <TableBody>
                                    {!this.state.loading &&
                                        displayedProductDiscounts.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={6 + shops.length * 2} // 5 info columns + 2 columns per shop
                                                    className={classNames(
                                                        classes.noProductsMessage,
                                                        classes.tableCell
                                                    )}
                                                >
                                                    <img
                                                        className={classes.noProductsIcon}
                                                        src={
                                                            staticRoot + 'web/img/product-gray.png'
                                                        }
                                                    />
                                                    <Typography
                                                        className={classes.noProductsText}
                                                        variant="subtitle1"
                                                    >
                                                        {gettext('No products found')}
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<AddIcon />}
                                                        className={classes.tableAddButton}
                                                        disabled={
                                                            !this.state.discount.store ||
                                                            isAddingProductsDisabled
                                                        }
                                                        onClick={this.handleAddProducts}
                                                    >
                                                        {gettext('Add products')}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    {displayedProductDiscounts.map((productDiscount) => {
                                        const isSelected = this.isSelected(
                                            productDiscount.product.id
                                        )
                                        const numStockUnits =
                                            productDiscount.product.stock_units.length
                                        const stockUnitDiscounts =
                                            productDiscount.stock_unit_discounts.map(
                                                (stockUnitDiscount) => {
                                                    return filterDuplicate(stockUnitDiscount)
                                                }
                                            )

                                        return (
                                            <Fragment key={productDiscount.product.id}>
                                                {stockUnitDiscounts.map(
                                                    (stockUnitDiscount, index) => {
                                                        const {
                                                            percentDiscount,
                                                            valueDiscount,
                                                            stock_unit: stockUnit,
                                                            channel_stock_unit_discounts:
                                                                channelStockUnitDiscounts,
                                                        } = stockUnitDiscount

                                                        return (
                                                            <TableRow
                                                                key={stockUnit.sku}
                                                                selected={isSelected}
                                                            >
                                                                {index === 0 && (
                                                                    <>
                                                                        <TableCell
                                                                            padding="checkbox"
                                                                            className={
                                                                                classes.tableCell
                                                                            }
                                                                            rowSpan={numStockUnits}
                                                                        >
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                onChange={(
                                                                                    _,
                                                                                    checked
                                                                                ) =>
                                                                                    this.handleChecked(
                                                                                        checked,
                                                                                        productDiscount
                                                                                    )
                                                                                }
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            component="th"
                                                                            scope="row"
                                                                            padding="none"
                                                                            className={
                                                                                classes.tableCell
                                                                            }
                                                                            rowSpan={numStockUnits}
                                                                        >
                                                                            <img
                                                                                className={
                                                                                    classes.productImg
                                                                                }
                                                                                src={
                                                                                    productDiscount
                                                                                        .product
                                                                                        .cover_image_url
                                                                                }
                                                                                height={50}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            className={
                                                                                classes.tableCell
                                                                            }
                                                                            rowSpan={numStockUnits}
                                                                        >
                                                                            <Typography
                                                                                className={
                                                                                    classes.truncatedDescription
                                                                                }
                                                                                variant="body2"
                                                                            >
                                                                                {getTranslation(
                                                                                    productDiscount.product,
                                                                                    language,
                                                                                    'name'
                                                                                )}
                                                                            </Typography>
                                                                        </TableCell>
                                                                    </>
                                                                )}

                                                                <TableCell
                                                                    className={classes.tableCell}
                                                                >
                                                                    <Typography variant="body2">
                                                                        {stockUnit.sku}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell
                                                                    className={classes.tableCell}
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        value={
                                                                            percentDiscount || ''
                                                                        }
                                                                        inputProps={{
                                                                            min: 0,
                                                                            max: getMaxDiscountPercentageValue(),
                                                                            style: {
                                                                                textAlign: 'right',
                                                                                fontSize: '0.9rem',
                                                                            },
                                                                        }}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <InputAdornment position="end">
                                                                                    %
                                                                                </InputAdornment>
                                                                            ),
                                                                        }}
                                                                        onChange={this.handleBulkDiscountPercentChange(
                                                                            productDiscount.product
                                                                                .id,
                                                                            stockUnit.sku
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                <TableCell
                                                                    className={classes.tableCell}
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        value={valueDiscount || ''}
                                                                        inputProps={{
                                                                            min: 0,
                                                                            style: {
                                                                                textAlign: 'right',
                                                                                fontSize: '0.9rem',
                                                                            },
                                                                        }}
                                                                        onChange={this.handleBulkDiscountValueChange(
                                                                            productDiscount.product
                                                                                .id,
                                                                            stockUnit.sku
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                {shops.map((shop) => {
                                                                    const channelStockUnitDiscount =
                                                                        channelStockUnitDiscounts.find(
                                                                            ({ shopID }) =>
                                                                                shopID === shop.id
                                                                        )

                                                                    if (
                                                                        !channelStockUnitDiscount?.channel_stock_unit
                                                                    ) {
                                                                        return (
                                                                            <Fragment key={shop.id}>
                                                                                <TableCell align="center">
                                                                                    <BlockIcon color="disabled" />
                                                                                </TableCell>
                                                                                <TableCell align="center">
                                                                                    <BlockIcon color="disabled" />
                                                                                </TableCell>
                                                                            </Fragment>
                                                                        )
                                                                    }

                                                                    const {
                                                                        channel_stock_unit:
                                                                            channelStockUnit,
                                                                        price,
                                                                        errors,
                                                                        shopID,
                                                                    } = channelStockUnitDiscount

                                                                    return (
                                                                        <Fragment key={shop.id}>
                                                                            <TableCell
                                                                                className={classNames(
                                                                                    classes.tableCell,
                                                                                    classes.originalPriceColumn
                                                                                )}
                                                                            >
                                                                                {Math.round(
                                                                                    channelStockUnit.price
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell
                                                                                className={
                                                                                    classes.tableCell
                                                                                }
                                                                            >
                                                                                <TextField
                                                                                    fullWidth
                                                                                    type="number"
                                                                                    size="small"
                                                                                    value={
                                                                                        parseFloat(
                                                                                            price
                                                                                        ) || ''
                                                                                    }
                                                                                    error={
                                                                                        !!errors.discountAmount
                                                                                    }
                                                                                    helperText={
                                                                                        errors.discountAmount
                                                                                    }
                                                                                    disabled={
                                                                                        !channelStockUnitDiscount
                                                                                    }
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        max: channelStockUnit.price,
                                                                                        style: {
                                                                                            textAlign:
                                                                                                'right',
                                                                                            fontSize:
                                                                                                '0.9rem',
                                                                                        },
                                                                                    }}
                                                                                    onChange={this.handleDiscountPriceChange(
                                                                                        productDiscount.product,
                                                                                        stockUnit,
                                                                                        shopID
                                                                                    )}
                                                                                />
                                                                            </TableCell>
                                                                        </Fragment>
                                                                    )
                                                                })}
                                                            </TableRow>
                                                        )
                                                    }
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            className={classes.bottomPagination}
                            component="div"
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            backIconButtonProps={{
                                'aria-label': gettext('Previous'),
                            }}
                            nextIconButtonProps={{
                                'aria-label': gettext('Next'),
                            }}
                            onPageChange={(_, newPage) => this.handleChangePage(newPage)}
                            rowsPerPageOptions={[rowsPerPage]}
                        />
                    </Paper>
                )}
                <div className={classes.bottomBar}>
                    <Button
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        component={Link}
                        to={getPath('discounts')}
                    >
                        {gettext('Back to discounts')}
                    </Button>
                    <div className={classes.saveActions}>
                        {this.state.saving && (
                            <CircularProgress className={classes.saveProgress} size={25} />
                        )}
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.saveButton}
                            onClick={this.saveHandler}
                            disabled={
                                (!this.discountComplete() && !isAddingProductsDisabled) ||
                                this.productDiscountHasErrors() ||
                                this.state.saving
                            }
                        >
                            {isCopy ? gettext('Create Copy') : gettext('Save Discount')}
                        </Button>
                    </div>
                </div>
                {this.state.discount.store && (
                    <ProductSelector
                        discountID={this.state.discount.id}
                        startDate={this.state.discount.start_date}
                        endDate={this.state.discount.end_date}
                        display={this.state.displayProductSelector}
                        productDiscounts={this.state.productDiscounts}
                        storeID={this.state.discount.store}
                        handleSelectedProducts={this.handleSelectedProducts}
                        excludedProductIDs={this.getExistedProductIDs()}
                    />
                )}
            </>
        )
    }
}

export default withStyles(styles)(Discount)
